
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { ImportDataType } from "../types";

interface FieldMapperProps {
  headers: string[];
  dataType: ImportDataType;
  onMapping: (mapping: Record<string, string>) => void;
  onBack: () => void;
}

export function FieldMapper({ headers, dataType, onMapping, onBack }: FieldMapperProps) {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  
  const getFieldOptions = () => {
    if (dataType === "clients") {
      return {
        required: ["name"],
        optional: ["industry", "website", "contact_email", "contact_phone", "address", "notes", "status"]
      };
    } 
    return {
      required: ["first_name", "last_name", "email"],
      optional: ["phone", "linkedin_url", "experience_years", "notes", "status", "source", "skills"]
    };
  };
  
  const { required, optional } = getFieldOptions();
  
  const handleFieldSelect = (dbField: string, csvHeader: string) => {
    setMapping(prev => ({
      ...prev,
      [dbField]: csvHeader
    }));
  };
  
  const canProceed = () => {
    return required.every(field => mapping[field] && mapping[field] !== "ignore");
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Map CSV Fields to Database Fields</h3>
      
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Required Fields</h4>
        {required.map(field => (
          <div key={field} className="grid grid-cols-2 gap-4 items-center">
            <div className="font-medium capitalize">{field.replace(/_/g, ' ')}</div>
            <select 
              className="border rounded p-2"
              value={mapping[field] || ""}
              onChange={(e) => handleFieldSelect(field, e.target.value)}
            >
              <option value="">Select CSV column</option>
              {headers.map(header => (
                <option key={header} value={header}>{header}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Optional Fields</h4>
        {optional.map(field => (
          <div key={field} className="grid grid-cols-2 gap-4 items-center">
            <div className="font-medium capitalize">{field.replace(/_/g, ' ')}</div>
            <select 
              className="border rounded p-2"
              value={mapping[field] || ""}
              onChange={(e) => handleFieldSelect(field, e.target.value)}
            >
              <option value="">Select CSV column</option>
              <option value="ignore">Ignore this field</option>
              {headers.map(header => (
                <option key={header} value={header}>{header}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          onClick={() => onMapping(mapping)} 
          disabled={!canProceed()}
        >
          Preview Import
        </Button>
      </div>
    </div>
  );
}
